/**
 * Gets an array of days representing the current month, including the days from the previous and next months
 * to fill the first and last weeks of the calendar view.
 *
 * @returns {number[]} An array of days for the current month, including padding days from the previous and next months.
 */
export const getCurrentMonthDays = (currentDate: Date) => {
	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();
	const firstDayOfMonth = (new Date(year, month, 1).getDay() + 6) % 7;

	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

	return [
		...previousMonthDays(month, year, firstDayOfMonth),
		...days,
		...nextMonthDays(firstDayOfMonth, daysInMonth)
	];
};

/**
 * Generates an array representing the days of the previous month that appear in the calendar view of the current month.
 *
 * @param month - The current month (0-based, where 0 = January, 11 = December).
 * @param year - The current year.
 * @param firstDayOfMonth - The day of the week the first day of the current month falls on (0 = Sunday, 6 = Saturday).
 * @returns {number[]} An array of negative numbers representing the days of the previous month that appear in the calendar view.
 */
export const previousMonthDays = (month: number, year: number, firstDayOfMonth: number): number[] => {
	const lastDayOfPreviousMonth = new Date(year, month, 0).getDate();
	return Array.from({ length: firstDayOfMonth }, (_, i) => -1 * (lastDayOfPreviousMonth - firstDayOfMonth + i + 1));
};

/**
 * Calculates the days of the next month that should be displayed in the current month's calendar view.
 *
 * @param firstDayOfMonth - The day of the week the current month starts on (0 for Sunday, 1 for Monday, ..., 6 for Saturday).
 * @param daysInMonth - The number of days in the current month.
 * @returns {number[]} An array of negative numbers representing the days of the next month to be displayed.
 */
export const nextMonthDays = (firstDayOfMonth: number, daysInMonth: number): number[] => {
	const nextMonthStartDay = (firstDayOfMonth + daysInMonth) % 7;
	const emptyDays = 7 - nextMonthStartDay;
	return Array.from({ length: emptyDays }, (_, i) => -1 * (i + 1));
};

/**
 * Checks if the provided day is a valid day in a month.
 *
 * @param day - The day to check, which can be a number or a string.
 * @returns {boolean} `true` if the day is a number greater than 0, otherwise `false`.
 */
export const isDayInMonth = (day: number | string): boolean => typeof day === 'number' && day > 0;

export const formatDate = (date: Date) => {
	const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
	return date.toLocaleDateString('pt-BR', options);
};

/**
 * Returns the first day of the week for the given date.
 * The week starts on Monday. If the given date is a Sunday,
 * it will return the previous Monday.
 *
 * @param date - The date for which to find the first day of the week.
 * @returns {Date} The first day of the week as a Date object.
 */
export const getFirstDayOfWeek = (date: Date): Date => {
	const day = date.getDay();
	const diff = (day === 0 ? -6 : 1) - day;
	const firstDay = new Date(date);
	firstDay.setDate(date.getDate() + diff);
	return firstDay;
};

/**
 * Returns the last day of the week for the given date.
 *
 * @param date - The date for which to find the last day of the week.
 * @returns {Date} The last day of the week as a Date object.
 */
export const getLastDayOfWeek = (date: Date): Date => {
	const firstDay = getFirstDayOfWeek(date);
	const lastDay = new Date(firstDay);
	lastDay.setDate(firstDay.getDate() + 6);
	return lastDay;
};

/**
 * Generates an array of strings representing the days of the week starting from a given date.
 * Each string contains the abbreviated name of the day and the day number.
 *
 * @param {Date} firstDayOfWeek - The date representing the first day of the week.
 * @returns {string[]} An array of strings where each string is in the format "DayName DayNumber".
 */
export const getStringWeekDaysStartingFromDate = (firstDayOfWeek: Date): string[] => {
	const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
	const weekDays = [];

	for (let i = 0; i < 7; i++) {
		const currentDay = new Date(firstDayOfWeek);
		currentDay.setDate(firstDayOfWeek.getDate() + i);
		const dayName = daysOfWeek[currentDay.getDay()];
		const dayNumber = currentDay.getDate();
		weekDays.push(`${dayName} ${dayNumber}`);
	}

	return weekDays;
};

/**
 * Adds a specified number of days to a given date.
 *
 * @param date - The date to which to add days.
 * @param days - The number of days to add.
 * @returns {Date} A new Date object with the specified number of days added.
 */
export const addDays = (date: Date, days: number): Date => {
	const newDate = new Date(date);
	newDate.setDate(date.getDate() + days);
	return newDate;
}

/**
 * Subtracts a specified number of days from a given date.
 *
 * @param date - The date from which to subtract days.
 * @param days - The number of days to subtract.
 * @returns {Date} A new Date object with the specified number of days subtracted.
 */
export const timeDifference = (past: Date): string => {
	const now = new Date();
	const diffInMs = now.getTime() - past.getTime();
	const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
	const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
	const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

	if (diffInMinutes < 60) {
		return `${diffInMinutes} min`;
	} else if (diffInHours < 24) {
		return `${diffInHours} h`;
	} else {
		return `${diffInDays} d`;
	}
};

/**
 * Converts a time string in the format "HH:mm" to the equivalent number of minutes.
 *
 * @param time - The time string to convert.
 * @returns {number} The equivalent number of minutes.
 */
export const convertTimeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(":").map(Number);
	return hours * 60 + minutes;
};

/**
 * Converts a time string in the format "HH:mm" to a Date object.
 *
 * @param time - The time string to convert.
 * @returns {Date} The Date object representing the time.
 */
export const diffBetweenDates = (startDate: Date, endDate: Date): string => {
	const diffMs = endDate.getTime() - startDate.getTime();
	const hours = Math.floor(diffMs / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
	return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Formats a time in milliseconds to a string in the format "DDD HH:MM".
 *
 * @param time - The time in milliseconds to format.
 * @returns {string} The formatted time string.
 */
export const formatMilisecondsInDaysHoursMinutes = (miliseconds: number): string => {
	const totalSeconds = Math.floor(miliseconds / 1000);
	const totalMinutes = Math.floor(totalSeconds / 60);

	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;

	const formattedDays = days > 0 ? `${days.toLocaleString('pt-BR')}D ` : '';
	const formattedHours = hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : '';
	const formattedMinutes = minutes > 0 ? 
		`${minutes.toString().padStart(2, '0')}m` : 
		minutes === 0 ? '00m' : '';

	return `${formattedDays}${formattedHours}${formattedMinutes}`;
}
/**
 * Formats a time in milliseconds to a string in the format "DDD HH:MM".
 *
 * @param time - The time in milliseconds to format.
 * @returns {string} The formatted time string.
 */
export const formatMilisecondsInDaysHours = (miliseconds: number): string => {
	const totalSeconds = Math.floor(miliseconds / 1000);
	const totalMinutes = Math.floor(totalSeconds / 60);

	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);

	const formattedDays = days > 0 ? `${days.toLocaleString('pt-BR')}D ` : '';
	const formattedHours = hours > 0 ? `${hours.toString().padStart(2, '0')}h ` : '';

	return `${formattedDays}${formattedHours}`;
}

/**
 * Formats milliseconds adaptively:
 * - If >= 1 day: shows days and hours (e.g., "2D 03h")
 * - If >= 1 hour but < 1 day: shows hours and minutes (e.g., "03h 45m")
 * - If < 1 hour: shows only minutes (e.g., "45m")
 *
 * @param miliseconds - The time in milliseconds to format.
 * @returns {string} The formatted time string.
 */
export const formatMilisecondsAdaptive = (miliseconds: number): string => {
	const totalSeconds = Math.floor(miliseconds / 1000);
	const totalMinutes = Math.floor(totalSeconds / 60);

	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;

	if (days > 0) {
		const formattedHours = `${hours.toString().padStart(2, '0')}h`;
		return `${days.toLocaleString('pt-BR')}D ${formattedHours}`;
	}

	if (hours > 0) {
		const formattedMinutes = `${minutes.toString().padStart(2, '0')}m`;
		return `${hours.toString().padStart(2, '0')}h ${formattedMinutes}`;
	}

	return `${totalMinutes}m`;
}

/**
 * Converts a time string in the format "HH:MM" to milliseconds.
 *
 * @param time - The time string to convert.
 * @returns {number} The equivalent number of milliseconds.
 */
export const convertTimeStringToMilliseconds = (time: string): number => {
	const [hours, minutes] = time.split(':').map(Number);
	return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
}

/**
 * Converts a time string in the format "DD HH:MM" to total minutes.
 * Example: "4D 3h 53m" -> 6233 minutes
 *
 * @param timeString - The time string to convert (e.g. "4D 3h 53m")
 * @returns {number} The total number of minutes
 */
export const convertTimeStringToMinutes = (timeString: string): number => {
	const daysMatch = timeString.match(/(\d+)D/);
	const hoursMatch = timeString.match(/(\d+)h/);
	const minutesMatch = timeString.match(/(\d+)m/);

	const days = daysMatch ? parseInt(daysMatch[1]) : 0;
	const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
	const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

	return (days * 24 * 60) + (hours * 60) + minutes;
}

/**
 * Applies a time mask to a string.
 * Example: "123" -> "12:30"
 * @param value - The string to apply the mask to.
 * @returns The string with the mask applied.
 */
export const applyTimeMask = (value: string): string => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara hh:mm
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}:${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}:${numbers.slice(2, 4)}`;
    }
  };


export const formatDateTimeWithTimezone = (dateString: string | Date): string => {
	let dateToFormat = dateString;
	if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T')) {
		dateToFormat = `${dateString}Z`;
	} else if (typeof dateString === 'string' && dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
		dateToFormat = `${dateString}Z`;
	}

	const date = new Date(dateToFormat);
	const formattedDate = date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
	const formattedTime = date.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'America/Sao_Paulo'
	});
	return `${formattedDate} - ${formattedTime}`;
};
